import { FC, useEffect } from 'react';
import { Box, Button, Container, Grow, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGenerateRegistrationOptionsQuery, useVerifyRegistrationResponseMutation } from '../../graphql/generated/operations/auth.ts';
import { Paths } from '../../constants/paths.ts';
import { faFingerprint } from '@fortawesome/free-solid-svg-icons';
import { startRegistration } from '@simplewebauthn/browser';
import { LoadingBackdrop } from '../../components/loading.backdrop.tsx';
import { trackEvent } from '../../utils/analytics.ts';

export const CreatePasskeyPage: FC = () => {
  const { data, loading: gettingConfig } = useGenerateRegistrationOptionsQuery();
  const [createPasskey, { data: passkey, loading: creatingPasskey }] = useVerifyRegistrationResponseMutation();
  const navigate = useNavigate();

  const createNewPasskey = async () => {
    if (!data?.generateRegistrationOptions) {
      return;
    }
    trackEvent('passkey_create_start');
    const options = data.generateRegistrationOptions.options;
    const attResp = await startRegistration(options);
    await createPasskey({ variables: { attResp } });
  };

  useEffect(() => {
    if (data?.generateRegistrationOptions.hasPasskey) {
      navigate(Paths.HOME);
    }
  }, [data]);

  useEffect(() => {
    if (passkey) {
      trackEvent('passkey_create_success');
      navigate(Paths.HOME);
    }
  }, [passkey]);

  return (
    <Grow in>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          pt: 15,
          pb: 10
        }}
      >
        <LoadingBackdrop open={gettingConfig || creatingPasskey} />
        <Container
          sx={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Typography variant="h3" paragraph>
            Create a Passkey
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            A passkey is a secure way to access your account. It is used to verify your identity and protect your data. This passkey can be used to automatically log you in to your
            account.
          </Typography>
          <Box sx={{ m: 10 }}>
            <FontAwesomeIcon icon={faFingerprint} size="5x" />
          </Box>
          <Box display="flex" justifyContent="center">
            <Stack gap={3} width="100%" maxWidth="sm">
              <Button size="large" fullWidth variant="contained" onClick={createNewPasskey}>
                Create Passkey
              </Button>
              <Button
                size="large"
                fullWidth
                variant="outlined"
                onClick={() => {
                  navigate(Paths.HOME);
                  trackEvent('passkey_create_skip');
                }}
              >
                Skip for now
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Grow>
  );
};
